"use client";

import { useRef, useEffect } from "react";
import { motion } from "framer-motion";
import LiveDemoWidget from "./LiveDemoWidget";

const VIDEO_URL =
  "https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260306_115329_5e00c9c5-4d69-49b7-94c3-9c31c60bb644.mp4";

export default function Hero() {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    // Force play — some browsers block autoPlay on hydration
    v.play().catch(() => {
      // Autoplay blocked (e.g. if muted isn't recognized), retry on interaction
      const handler = () => { v.play().catch(() => {}); document.removeEventListener("click", handler); };
      document.addEventListener("click", handler, { once: true });
    });
  }, []);

  return (
    <section
      id="hero"
      style={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        background: "#0a0c10",
      }}
    >
      {/* Full-screen background video */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        crossOrigin="anonymous"
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
        }}
      >
        <source src={VIDEO_URL} type="video/mp4" />
      </video>

      {/* Dark overlay for text readability */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.55) 100%)",
          zIndex: 1,
        }}
      />

      {/* Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        style={{
          position: "relative",
          zIndex: 10,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          padding: "140px 24px 100px",
          maxWidth: 900,
          width: "100%",
        }}
      >
        <h1
          style={{
            fontFamily: "var(--font-heading)",
            fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
            color: "#fff",
            marginBottom: 24,
            lineHeight: 1.1,
          }}
        >
          AI Voice Agents for Scalable Calling,{" "}
          <em style={{ fontFamily: "var(--font-heading)" }}>Follow Up</em>, and
          Multilingual Outreach
        </h1>
        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: "clamp(1rem, 1.5vw, 1.2rem)",
            color: "rgba(255,255,255,0.82)",
            fontWeight: 300,
            lineHeight: 1.75,
            maxWidth: 640,
            margin: "0 auto",
          }}
        >
          Built for market research firms and call center teams worldwide.
          EthnicPath Virtual helps you launch voice campaigns that feel
          personal, work around the clock, and drive measurable results.
        </p>

        <p
          style={{
            fontFamily: "var(--font-body)",
            fontSize: "0.92rem",
            color: "rgba(255,255,255,0.92)",
            fontWeight: 400,
            lineHeight: 1.5,
            marginTop: 28,
            padding: "10px 22px",
            borderRadius: 999,
            border: "1px solid rgba(255,255,255,0.25)",
            background: "rgba(255,255,255,0.08)",
          }}
        >
          Every agent is custom-built for your business and live the same day.
        </p>

        <div
          style={{
            display: "flex",
            gap: 14,
            justifyContent: "center",
            marginTop: 24,
            flexWrap: "wrap",
          }}
        >
          <LiveDemoWidget />
          <a
            href="#pricing"
            className="liquid-glass-cta btn-hero"
            style={{ padding: "16px 40px", fontSize: "1rem" }}
          >
            View Plans
          </a>
          <a
            href="#features"
            className="btn-hero-outline"
            style={{ padding: "16px 40px", fontSize: "1rem" }}
          >
            Learn More
          </a>
        </div>
      </motion.div>
    </section>
  );
}

